import { useState, useMemo } from "react";
import "./RecommendationView.css";
import MobileFrame from "../MobileFrame";
import { MOCK_REPAIR_SHOPS, RepairShop, FaultCode, formatCost, formatTime } from "../repairData";

interface DiagnosisResult {
    fault: FaultCode;
    vehicleNumber: string;
}

interface RecommendationViewProps {
    diagnosisResult: DiagnosisResult | null;
    selectedShop: RepairShop | null;
    setSelectedShop: (s: RepairShop | null) => void;
    onNext: () => void;
}

type SortKey = "distance" | "cost" | "time";

const MAP_ROADS_H = [22, 48, 72];
const MAP_ROADS_V = [25, 50, 75];

// ── Mobile App: Shop List ─────────────────────────────────────────────────
function MobileShopList({
    shops, selectedShop, onSelect, onConfirm,
}: {
    shops: RepairShop[];
    selectedShop: RepairShop | null;
    onSelect: (s: RepairShop) => void;
    onConfirm: () => void;
}) {
    return (
        <>
            {/* App header */}
            <div className="mapp-header gradient">
                <div className="mapp-header-text">
                    <div className="mapp-header-title">정비소 추천</div>
                    <div className="mapp-header-sub">주변 트럭 정비 가능 업체</div>
                </div>
                <div className="mapp-logo-badge">STEP 5</div>
            </div>

            {/* Location bar */}
            <div style={{
                padding: "8px 10px",
                background: "white",
                borderBottom: "1px solid #e5e5ea",
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "#374151",
                flexShrink: 0,
            }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                </svg>
                <span style={{ fontWeight: 600, color: "#0d9488" }}>현재 위치</span>
                <span style={{ color: "#6b7280" }}>부산 동구 초량동 — 반경 10km</span>
                <span style={{ marginLeft: "auto", background: "#f0fdf4", color: "#16a34a", fontWeight: 600, fontSize: 11, padding: "2px 8px", borderRadius: 4 }}>
                    {shops.length}곳
                </span>
            </div>

            {/* Shop cards */}
            <div style={{ flex: 1, overflow: "auto", paddingBottom: 8 }}>
                {shops.map((shop, idx) => (
                    <div
                        key={shop.id}
                        className={`mapp-shop-card ${selectedShop?.id === shop.id ? "selected" : ""}`}
                        onClick={() => onSelect(shop)}
                    >
                        <div className="mapp-shop-top">
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span style={{
                                        width: 18, height: 18, borderRadius: "50%",
                                        background: idx === 0 ? "#0d9488" : "#e5e5ea",
                                        color: idx === 0 ? "white" : "#6b7280",
                                        fontSize: 10, fontWeight: 700,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                    }}>{idx + 1}</span>
                                    <div className="mapp-shop-name">{shop.name}</div>
                                </div>
                                <div className="mapp-shop-addr">{shop.address}</div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                                <span className="mapp-shop-cap">{shop.capability}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, color: "#f59e0b", fontWeight: 600 }}>
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
                                    {shop.rating}
                                </div>
                            </div>
                        </div>
                        <div className="mapp-shop-metrics">
                            <div className="mapp-shop-metric">
                                <span className="mapp-metric-label">거리</span>
                                <span className="mapp-metric-value cyan">{shop.distance}km</span>
                            </div>
                            <div className="mapp-shop-metric">
                                <span className="mapp-metric-label">예상 시간</span>
                                <span className="mapp-metric-value">{formatTime(shop.estimatedTime)}</span>
                            </div>
                            <div className="mapp-shop-metric">
                                <span className="mapp-metric-label">예상 비용</span>
                                <span className="mapp-metric-value" style={{ fontSize: 11 }}>{formatCost(shop.estimatedCost)}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* CTA */}
            <div className="mapp-cta">
                <button
                    className="mapp-cta-btn"
                    disabled={!selectedShop}
                    onClick={onConfirm}
                >
                    {selectedShop ? `${selectedShop.name} 선택 완료` : "정비소를 선택하세요"}
                    {selectedShop && (
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                        </svg>
                    )}
                </button>
            </div>
        </>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function RecommendationView({
    diagnosisResult,
    selectedShop,
    setSelectedShop,
    onNext,
}: RecommendationViewProps) {
    const [sortKey, setSortKey] = useState<SortKey>("distance");

    const sortedShops = useMemo(() => {
        const copy = [...MOCK_REPAIR_SHOPS];
        copy.sort((a, b) => {
            if (sortKey === "distance") return a.distance - b.distance;
            if (sortKey === "cost") return a.estimatedCost - b.estimatedCost;
            return a.estimatedTime - b.estimatedTime;
        });
        return copy;
    }, [sortKey]);

    return (
        <div className="rec-page">
            {/* ── Mobile Side ── */}
            <div className="rec-mobile-side">
                <MobileFrame label="운전자 앱 화면">
                    <MobileShopList
                        shops={sortedShops}
                        selectedShop={selectedShop}
                        onSelect={setSelectedShop}
                        onConfirm={onNext}
                    />
                </MobileFrame>
            </div>

            {/* ── Web Side: Map + Detail ── */}
            <div className="rec-web-side">
                {/* Scenario step indicator */}
                <div className="rec-scenario-banner">
                    <span className="rec-scenario-label">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                        </svg>
                        시나리오 — 비실시간 정적 대응형
                    </span>
                    <span className="rec-active-step">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                        </svg>
                        STEP 5 — 정비소 추천 (대응 가능 여부·소요시간·예상비용 제시)
                    </span>
                    {diagnosisResult && (
                        <span style={{
                            fontSize: 12, fontWeight: 600, color: "#475569",
                            background: "#f8fafc", padding: "5px 12px", borderRadius: 6,
                            border: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 6,
                            whiteSpace: "nowrap",
                        }}>
                            <span style={{ color: "#9ca3af" }}>진단:</span>
                            {diagnosisResult.fault.lightName}
                            <span className={`diag-sev-badge ${diagnosisResult.fault.severity}`}
                                style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999 }}
                            >
                                {diagnosisResult.fault.severity}
                            </span>
                        </span>
                    )}
                </div>

                {/* Sort toolbar */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 16px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                        트럭 정비 가능 업체 {sortedShops.length}곳
                    </span>
                    <div style={{ display: "flex", gap: 6 }}>
                        {(["distance", "cost", "time"] as SortKey[]).map((k) => (
                            <button
                                key={k}
                                onClick={() => setSortKey(k)}
                                style={{
                                    padding: "5px 12px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    borderRadius: 6,
                                    border: "1px solid",
                                    borderColor: sortKey === k ? "#0ea5e9" : "#e2e8f0",
                                    background: sortKey === k ? "#f0f9ff" : "white",
                                    color: sortKey === k ? "#0284c7" : "#6b7280",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                }}
                            >
                                {k === "distance" ? "거리순" : k === "cost" ? "가격순" : "시간순"}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Map card */}
                <div className="rec-map-card">
                    <div className="rec-map-card-header">
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="6" x2="15" y2="18" />
                            </svg>
                            지도 — 주변 정비소 위치
                        </span>
                        {selectedShop && (
                            <span style={{ fontSize: 12, color: "#0d9488", fontWeight: 600 }}>
                                선택: {selectedShop.name}
                            </span>
                        )}
                    </div>

                    {/* Map */}
                    <div className="rec-map-area">
                        <div className="rec-map-grid" />
                        {MAP_ROADS_H.map(t => <div key={t} className="rec-map-road-h" style={{ top: `${t}%` }} />)}
                        {MAP_ROADS_V.map(l => <div key={l} className="rec-map-road-v" style={{ left: `${l}%` }} />)}

                        {sortedShops.map(shop => (
                            <div
                                key={shop.id}
                                className="rec-map-pin"
                                style={{ left: `${shop.lng}%`, top: `${shop.lat}%` }}
                                onClick={() => setSelectedShop(shop)}
                            >
                                <div className={`rec-map-pin-bubble ${selectedShop?.id === shop.id ? "selected" : ""}`}>
                                    {shop.name.slice(0, 5)}
                                </div>
                                <div className={`rec-map-pin-tail ${selectedShop?.id === shop.id ? "selected" : ""}`} />
                            </div>
                        ))}

                        <div className="rec-map-pin" style={{ left: "50%", top: "55%" }}>
                            <div className="rec-map-pin-bubble my-loc">내 위치</div>
                            <div className="rec-map-pin-tail my-loc" />
                        </div>

                        <div className="rec-map-label">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                            </svg>
                            부산 동구 일대 — 반경 10km
                        </div>
                    </div>

                    {/* Selected shop bar */}
                    {selectedShop ? (
                        <div className="rec-selected-bar">
                            <div className="rec-selected-info">
                                <div className="rec-selected-name">{selectedShop.name}</div>
                                <div className="rec-selected-meta">
                                    <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>{formatTime(selectedShop.estimatedTime)}</span>
                                    <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>{formatCost(selectedShop.estimatedCost)}</span>
                                    <span><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>{selectedShop.distance}km · {selectedShop.capability}</span>
                                </div>
                            </div>
                            <button className="rec-request-btn" onClick={onNext}>
                                이 업체로 수리 요청
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                                </svg>
                            </button>
                        </div>
                    ) : (
                        <div className="rec-empty-bar">
                            앱 목록에서 정비소를 선택하면 수리 요청을 보낼 수 있습니다
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
